import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ErrorAlert } from '../components/Alerts';
import Loading from '../components/Loading';
import apiService from '../services/api';
import { JobPosition, JobShift, StoreUser } from '../types';
import CustomDropdown from '../components/CustomDropdown';
import DateSelector from '../components/DateSelector';
import { useAuth } from '../auth/AuthContext';
import { calcTotalPayment } from '../utils/salaryUtils';
import { isActiveApplicationStatus } from '../constants/applicationStatus';

const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          overflow: 'hidden',
        },
      },
    },
  },
});

// 時間文字列（HH:MM）から時間差（h）を計算
const calcDurationHours = (start: string, end: string): string => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let diff = (eh * 60 + em) - (sh * 60 + sm);
  if (diff <= 0) diff += 24 * 60; // 深夜跨ぎ対応
  const hours = diff / 60;
  return `${hours.toFixed(1)}h`;
};

// 深夜跨ぎの終了時刻表示（end ≤ startなら「翩HH:MM」）
const formatEndTime = (start: string, end: string): string => {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  return (eh * 60 + em) <= (sh * 60 + sm) ? `翌${end}` : end;
};

// シフト開始時刻が過ぎているか判定
const isShiftExpired = (shift: JobShift): boolean => {
  const shiftDate = new Date(shift.shift_date);
  const now = new Date();
  const jstFormatter = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const nowDateStr = jstFormatter.format(now);
  const shiftDateStr = jstFormatter.format(shiftDate);
  if (shiftDateStr < nowDateStr) return true;
  if (shiftDateStr === nowDateStr) {
    const jstTimeFormatter = new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const timeStr = jstTimeFormatter.format(now);
    const [currentHours, currentMinutes] = timeStr.split(':').map(Number);
    const [shiftHours, shiftMinutes] = shift.start_time.split(':').map(Number);
    if (shiftHours < currentHours || (shiftHours === currentHours && shiftMinutes < currentMinutes)) {
      return true;
    }
  }
  return false;
};

// 実報酬合計の計算は src/utils/salaryUtils.ts の calcTotalPayment を使用

const JobList: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<JobPosition[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [jobCounts, setJobCounts] = useState<Record<string, number>>({});
  const [appliedDates, setAppliedDates] = useState<Record<string, number>>({});
  const [shiftsLoading, setShiftsLoading] = useState<boolean>(false);
  const [selectedDateShifts, setSelectedDateShifts] = useState<JobShift[]>([]);
  const [searchResult, setSearchResult] = useState<JobShift[]>([]);
  const [stores, setStores] = useState<StoreUser[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [allShifts, setAllShifts] = useState<JobShift[]>([]);

  const { userId } = useAuth();

    const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

  const formatShiftDate = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const w = WEEKDAYS[d.getDay()];
    return `${y}.${m}.${day}(${w})`;
  };


  const filterShifts = useCallback(() => {
    let filtered: JobShift[] = [...selectedDateShifts];
    if (selectedLocation) {
      filtered = filtered.filter((job) => job.store_id === selectedLocation);
    }
    // 出勤予定（進行中の応募）を先頭に並べ替え（同グループ内の既存順は維持）
    // user_has_applied は不採用・キャンセル済みも含む旗なので、ここでは使わない。
    filtered.sort(
      (a, b) =>
        (isActiveApplicationStatus(b.user_application_status, !!b.user_has_applied) ? 1 : 0) -
        (isActiveApplicationStatus(a.user_application_status, !!a.user_has_applied) ? 1 : 0)
    );
    setSearchResult(filtered);
  }, [selectedLocation, selectedDateShifts]);

  useEffect(() => {
    const fetchJobs = async () => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const storeUserResponse = await apiService.getStoreUser(userId);
        if (storeUserResponse.success && storeUserResponse.data) {
          setStores(storeUserResponse.data);
        }

        const jobsResponse = await apiService.getAvailableJob();
        if (jobsResponse.success && jobsResponse.data) {
          setDepartments(jobsResponse.data);
        }

        const today = new Date();
        const formattedToday = formatDate(today, 'yyyy-MM-dd');

        const shiftsResponse = await apiService.getAvailableShifts(userId, formattedToday);

        if (shiftsResponse.success && shiftsResponse.data) {
          const counts: Record<string, number> = {};
          let allShifts: JobShift[] = [];

          if (shiftsResponse.data.shifts && Array.isArray(shiftsResponse.data.shifts)) {
            allShifts = shiftsResponse.data.shifts as JobShift[];
            allShifts.forEach(shift => {
              if (shift.is_full) return;
              const date = shift.shift_date;
              counts[date] = (counts[date] || 0) + 1;
            });
          } else if (typeof shiftsResponse.data.shifts === 'object') {
            const shiftsByDate = shiftsResponse.data.shifts as Record<string, JobShift[]>;
            Object.keys(shiftsByDate).forEach(date => {
              const shifts = shiftsByDate[date];
              if (Array.isArray(shifts)) {
                counts[date] = shifts.filter(s => !s.is_full).length;
                allShifts.push(...shifts);
              }
            });
          }

          setJobCounts(counts);
          setAllShifts(allShifts);

          // 応募済みシフトの日付ごとの集計
          const applied: Record<string, number> = {};
          allShifts.forEach(shift => {
            if (isActiveApplicationStatus(shift.user_application_status, !!shift.user_has_applied)) {
              const date = shift.shift_date;
              applied[date] = (applied[date] || 0) + 1;
            }
          });
          setAppliedDates(applied);

          const todayShifts = allShifts.filter(shift => shift.shift_date === formattedToday);
          setSelectedDateShifts(todayShifts);
          setSearchResult(todayShifts);
        } else {
          setError(shiftsResponse.error || '求人情報の取得に失敗しました');
        }
      } catch (error) {
        setError('求人情報の取得中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [userId]);

  useEffect(() => {
    filterShifts();
  }, [filterShifts]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    fetchShiftsForDate(date, selectedLocation || undefined);
  };

  const recalcJobCounts = (shifts: JobShift[], storeId: string) => {
    const filtered = storeId ? shifts.filter(s => s.store_id === storeId) : shifts;
    const counts: Record<string, number> = {};
    const applied: Record<string, number> = {};
    filtered.forEach(shift => {
      if (!shift.is_full) {
        counts[shift.shift_date] = (counts[shift.shift_date] || 0) + 1;
      }
      if (isActiveApplicationStatus(shift.user_application_status, !!shift.user_has_applied)) {
        applied[shift.shift_date] = (applied[shift.shift_date] || 0) + 1;
      }
    });
    setJobCounts(counts);
    setAppliedDates(applied);
  };

  const handleLocationChange = (locationId: string) => {
    setSelectedLocation(locationId);
    recalcJobCounts(allShifts, locationId);
    fetchShiftsForDate(selectedDate, locationId || undefined);
  };

  const fetchShiftsForDate = async (date: Date, storeId?: string) => {
    const formattedDate = formatDate(date, 'yyyy-MM-dd');
    setShiftsLoading(true);
    try {
      const response = await apiService.getAvailableShifts(userId, formattedDate, 1);
      if (response.success && response.data) {
        let shiftsData: JobShift[] = [];
        if (response.data.shifts && Array.isArray(response.data.shifts)) {
          shiftsData = response.data.shifts as JobShift[];
        } else if (typeof response.data.shifts === 'object') {
          const shiftsByDate = response.data.shifts as Record<string, JobShift[]>;
          shiftsData = shiftsByDate[formattedDate] || [];
        }
        if (storeId) {
          shiftsData = shiftsData.filter(shift => shift.store_id === storeId);
        }
        setSelectedDateShifts(shiftsData);
        setSearchResult(shiftsData);
      } else {
        setError(response.error || '求人シフトの取得に失敗しました');
        setSelectedDateShifts([]);
        setSearchResult([]);
      }
    } catch (error) {
      setError('求人シフトの取得中にエラーが発生しました');
      setSelectedDateShifts([]);
      setSearchResult([]);
    } finally {
      setShiftsLoading(false);
    }
  };

  const formatDate = (date: Date, fmt: string): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return fmt
      .replace('yyyy', year.toString())
      .replace('MM', month)
      .replace('dd', day);
  };

  const storeOptions = [
    { value: '', label: 'すべての店舗の求人' },
    ...stores.map(store => ({ value: store.store_id, label: store.store_name })),
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          bgcolor: '#f8f8f9',
          // ヘッダー(80px)+フッターナビ(80px)+iOS safe areaを引いた高さで
          // スクロールコンテナの下端がフッターナビと重ならないようにする
          // dvh: Safari動的ビューポート対応（タブバー表示時にも正しい高さになる）
          height: 'calc(100dvh - 160px - env(safe-area-inset-bottom, 0px))',
          overflow: 'auto',
          width: '100%',
          paddingBottom: '24px',
        }}
      >
        {/* ヘッダー */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            pt: 1.5,
            pb: 1,
            gap: 0.5,
          }}
        >
          <IconButton sx={{ color: '#9e9e9e', p: 0.5 }}>
            <SearchIcon sx={{ fontSize: 22 }} />
          </IconButton>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#3c3c3c', fontSize: '1.1rem' }}>
            求人を探す
          </Typography>
        </Box>

        {/* 日付タブ（31日間・横スクロール） */}
        <DateSelector
          onChange={handleDateSelect}
          jobCounts={jobCounts}
          appliedDates={appliedDates}
        />

        {/* 店舗ドロップダウン */}
        <Box sx={{ px: 2 }}>
          <CustomDropdown
            label="すべての店舗の求人"
            options={storeOptions}
            value={selectedLocation}
            onChange={handleLocationChange}
          />
        </Box>

        {error && (
          <Box sx={{ px: 2 }}>
            <ErrorAlert message={error} onClose={() => setError(null)} />
          </Box>
        )}

        {/* 求人リスト */}
        {loading || shiftsLoading ? (
          <Loading message="求人情報を読み込み中..." />
        ) : (
          <Box sx={{ px: 2, pt: 0.5 }}>
            {searchResult.length > 0 ? (
              searchResult.map((shift) => {
                const remaining = shift.required_workers - shift.current_applicants;
                const fillRate = shift.required_workers > 0
                  ? (shift.current_applicants / shift.required_workers) * 100
                  : 0;
                const duration = calcDurationHours(shift.start_time, shift.end_time);
                const totalPayment = calcTotalPayment(
                  shift.salary_amount,
                  shift.salary_type,
                  shift.start_time,
                  shift.end_time,
                  shift.break_duration_minutes ?? 0,
                  shift.salary_allowance,
                  shift.salary_transport
                );
                const hasAllowance = Number(shift.salary_allowance) > 0;
                const hasTransport = Number(shift.salary_transport) > 0;
                const dateLabel = formatShiftDate(shift.shift_date);
                const expired = isShiftExpired(shift);
                const isUnavailable = expired || shift.is_full;

                return (
                  <Card
                    key={shift.id}
                    sx={{
                      mb: 2,
                      borderRadius: '12px',
                      boxShadow: 2,
                      bgcolor: isUnavailable ? 'rgba(0,0,0,0.04)' : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* 上段：日付 + 出勤予定/募集中チップ */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {dateLabel}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {/* 不採用・キャンセル済みの応募に「出勤予定」は出さない
                              （user_has_applied はそれらも含む＝再応募不可の旗） */}
                          {isActiveApplicationStatus(
                            shift.user_application_status,
                            !!shift.user_has_applied
                          ) && (
                            <Chip
                              label={shift.user_work_completed ? '勤務完了' : '出勤予定'}
                              size="small"
                              sx={{
                                bgcolor: shift.user_work_completed ? '#e3f2fd' : '#e8f5e9',
                                color: shift.user_work_completed ? '#1565c0' : '#2e7d32',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          )}
                          {expired ? (
                            <Chip
                              label="募集終了"
                              size="small"
                              color="error"
                              sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: 22 }}
                            />
                          ) : !shift.is_full ? (
                            <Chip
                              label="募集中"
                              size="small"
                              sx={{
                                bgcolor: '#d0f3ee',
                                color: '#13c3a8',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          ) : (
                            <Chip
                              label="満員"
                              size="small"
                              sx={{
                                bgcolor: '#fce4ec',
                                color: '#c62828',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 22,
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* 時間ブロック + 店舗名・求人タイトル・給与 */}
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
                        {/* 時間ブロック */}
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            bgcolor: '#f3f3f3',
                            border: '1px solid #e7e7e7',
                            borderRadius: '8px',
                            px: 1,
                            py: 0.75,
                            minWidth: 56,
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', lineHeight: 1.2, color: '#898989' }}>
                            {shift.start_time}
                          </Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: '#898989', lineHeight: 1 }}>▼</Typography>
                          <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', lineHeight: 1.2, color: '#898989' }}>
                            {formatEndTime(shift.start_time, shift.end_time)}
                          </Typography>
                          {duration && (
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', mt: 0.25, color: '#898989' }}>
                              {duration}
                            </Typography>
                          )}
                        </Box>

                        {/* 店舗名・求人タイトル・給与 */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                            {shift.store_name}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            sx={{ lineHeight: 1.3, mb: 0.5, color: isUnavailable ? 'text.disabled' : 'text.primary' }}
                          >
                            {shift.job_title}
                          </Typography>
                          {totalPayment !== null && (
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{ color: isUnavailable ? 'text.disabled' : 'text.primary', lineHeight: 1.2 }}
                            >
                              ¥{totalPayment.toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* 求人状況プログレスバー */}
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            求人状況
                          </Typography>
                          {!isUnavailable && remaining > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              残り{remaining}名
                            </Typography>
                          )}
                        </Box>
                        {/* グラデーション進捗バー */}
                        <Box
                          sx={{
                            position: 'relative',
                            height: 8,
                            borderRadius: 4,
                            bgcolor: '#e0e0e0',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              height: '100%',
                              width: `${isUnavailable ? 100 : Math.min(fillRate, 100)}%`,
                              borderRadius: 4,
                              background: 'linear-gradient(to right, #f5a623, #f07020, #e03010)',
                              transition: 'width 0.4s ease',
                            }}
                          />
                        </Box>
                      </Box>

                      {/* 手当・交通費チップ */}
                      {(hasAllowance || hasTransport) && (
                        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                          {hasAllowance && (
                            <Chip
                              label="手当あり"
                              size="small"
                              sx={{
                                bgcolor: '#fbddd0',
                                color: '#ea5514',
                                fontSize: '0.7rem',
                                height: 22,
                                fontWeight: 500,
                              }}
                            />
                          )}
                          {hasTransport && (
                            <Chip
                              label="交通費あり"
                              size="small"
                              sx={{
                                bgcolor: '#fbddd0',
                                color: '#ea5514',
                                fontSize: '0.7rem',
                                height: 22,
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>
                      )}

                      {/* 詳細ボタン */}
                      <Button
                        fullWidth
                        variant="contained"
                        component={RouterLink}
                        to={`/jobs/${shift.template_id}?shift=${shift.id}${shift.user_has_applied ? '&applied=1' : ''}${shift.user_work_completed ? '&completed=1' : ''}`}
                        disabled={isUnavailable && !shift.user_has_applied}
                        sx={{
                          bgcolor: '#0071e3',
                          borderRadius: '8px',
                          fontWeight: 'bold',
                          py: 1,
                          fontSize: '0.9rem',
                          '&:hover': { bgcolor: '#0058b7' },
                        }}
                      >
                        {expired && !shift.user_has_applied ? '募集終了' : shift.is_full && !shift.user_has_applied ? '満員です' : '詳細を確認する'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary">
                  該当の求人募集はありません
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default JobList;
